import React, { useEffect, useMemo, useState } from "react";
import { api, networkLabel } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { CheckCircle2, AlertCircle, Plus, Trash2, RefreshCw, PowerOff, Power } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

function StatusRow({ name, connected, sub, mocked }) {
  return (
    <div className="flex items-start justify-between p-5 border border-zinc-200 bg-white">
      <div>
        <div className="flex items-center gap-2">
          <div className="font-display font-semibold uppercase tracking-tight">{name}</div>
          {mocked && (
            <span className="px-1.5 py-0.5 text-[10px] font-mono uppercase bg-amber-50 text-amber-700 border border-amber-200">
              Mocked
            </span>
          )}
        </div>
        {sub && <div className="text-xs text-zinc-500 font-mono mt-1">{sub}</div>}
      </div>
      <div>
        {connected ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-mono border bg-emerald-50 text-emerald-700 border-emerald-200">
            <CheckCircle2 className="w-3 h-3" /> CONNECTED
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-mono border bg-red-50 text-red-700 border-red-200">
            <AlertCircle className="w-3 h-3" /> DISCONNECTED
          </span>
        )}
      </div>
    </div>
  );
}

export default function Settings() {
  const [integrations, setIntegrations] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newAcct, setNewAcct] = useState({ account_id: "", name: "", network: "google", currency: "GBP" });
  const [removeTarget, setRemoveTarget] = useState(null);
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const [showDisabled, setShowDisabled] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [i, a] = await Promise.all([
        api.get("/integrations"),
        api.get("/accounts", { params: { include_disabled: true } }),
      ]);
      setIntegrations(i.data);
      setAccounts(a.data.accounts || []);
    } catch (e) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addAccount = async () => {
    if (!newAcct.account_id.trim() || !newAcct.name.trim()) {
      return toast.error("Account ID and name are required");
    }
    try {
      await api.post("/accounts/mapping", newAcct);
      toast.success("Account mapping saved");
      setNewAcct({ account_id: "", name: "", network: "google", currency: "GBP" });
      load();
    } catch (e) {
      toast.error("Failed to save");
    }
  };

  const removeAccount = async () => {
    if (!removeTarget) return;
    try {
      await api.delete(`/accounts/mapping/${removeTarget}`);
      toast.success("Removed");
      setRemoveTarget(null);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const disconnectAccount = async () => {
    if (!disconnectTarget) return;
    try {
      await api.post(`/accounts/${disconnectTarget}/disconnect`);
      toast.success("Account disconnected");
      setDisconnectTarget(null);
      load();
    } catch (e) { toast.error("Failed to disconnect"); }
  };

  const reconnectAccount = async (account_id) => {
    try {
      await api.post(`/accounts/${account_id}/reconnect`);
      toast.success("Account reconnected");
      load();
    } catch (e) { toast.error("Failed to reconnect"); }
  };

  const visibleAccounts = useMemo(
    () => accounts.filter((a) => showDisabled || !a.disabled),
    [accounts, showDisabled]
  );

  return (
    <div data-testid="settings-page" className="fade-in">
      <PageHeader
        eyebrow="API Connections"
        title="Settings & Integrations"
        description="Check connection status of data sources and configure ad-account → network mappings."
        actions={
          <Button onClick={load} variant="outline" className="rounded-none" data-testid="settings-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8 space-y-8">
        {/* Integrations */}
        <section>
          <div className="data-label mb-3">Integrations</div>
          <div className="grid grid-cols-1 gap-3">
            <StatusRow
              name="ThoughtMetric"
              connected={integrations?.thoughtmetric?.connected}
              sub={`Project ID: ${integrations?.thoughtmetric?.project_id || "—"} · Used to read campaign performance & CPA`}
            />
            <StatusRow
              name="Meta Ads"
              connected={integrations?.meta_ads?.connected}
              sub={
                integrations?.meta_ads?.info?.name
                  ? `Authorized as ${integrations.meta_ads.info.name} · Used to push budget updates`
                  : "Used to push budget updates"
              }
            />
            <StatusRow
              name="Google Ads"
              connected={integrations?.google_ads?.connected}
              mocked={integrations?.google_ads?.mocked}
              sub={integrations?.google_ads?.info ||
                "Connect a Developer Token + OAuth to push live updates."}
            />
          </div>
        </section>

        {/* Accounts */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <div className="data-label">Ad Accounts</div>
            <label className="flex items-center gap-2 text-xs font-mono text-zinc-600 cursor-pointer">
              <input
                type="checkbox"
                data-testid="show-disabled-toggle"
                checked={showDisabled}
                onChange={(e) => setShowDisabled(e.target.checked)}
                className="accent-zinc-900"
              />
              Show disconnected
            </label>
          </div>
          <div className="bg-white border border-zinc-200">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse" data-testid="accounts-table">
                <thead>
                  <tr className="bg-zinc-50">
                    <th className="text-left px-5 py-3 data-label">Account ID</th>
                    <th className="text-left px-5 py-3 data-label">Name</th>
                    <th className="text-left px-5 py-3 data-label">Network</th>
                    <th className="text-left px-5 py-3 data-label">Currency</th>
                    <th className="text-left px-5 py-3 data-label">Status</th>
                    <th className="text-right px-5 py-3 data-label">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleAccounts.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-8 text-zinc-500 font-mono text-sm">No ad accounts.</td></tr>
                  )}
                  {visibleAccounts.map((a) => (
                    <tr key={a.account_id} className={`border-b border-zinc-100 ${a.disabled ? "opacity-50" : ""}`} data-testid={`account-row-${a.account_id}`}>
                      <td className="px-5 py-3 font-mono">{a.account_id}</td>
                      <td className="px-5 py-3">{a.name}</td>
                      <td className="px-5 py-3">
                        <span className="px-2 py-0.5 text-xs font-mono uppercase border border-zinc-300">
                          {networkLabel(a.network)}
                        </span>
                      </td>
                      <td className="px-5 py-3 font-mono text-xs text-zinc-600">{a.currency || "—"}</td>
                      <td className="px-5 py-3 text-xs font-mono">
                        {a.disabled ? (
                          <span className="text-red-700 inline-flex items-center gap-1"><PowerOff className="w-3 h-3" />DISCONNECTED</span>
                        ) : (
                          <span className="text-emerald-700 inline-flex items-center gap-1"><Power className="w-3 h-3" />CONNECTED</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          {a.disabled ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-none h-8 text-xs"
                              onClick={() => reconnectAccount(a.account_id)}
                              data-testid={`reconnect-account-${a.account_id}`}
                            >
                              <Power className="w-3.5 h-3.5 mr-1" /> Reconnect
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-none h-8 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
                              onClick={() => setDisconnectTarget(a.account_id)}
                              data-testid={`disconnect-account-${a.account_id}`}
                            >
                              <PowerOff className="w-3.5 h-3.5 mr-1" /> Disconnect
                            </Button>
                          )}
                          {a.network === "google" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="rounded-none h-8 text-xs text-red-600 hover:text-red-700"
                              onClick={() => setRemoveTarget(a.account_id)}
                              data-testid={`remove-account-${a.account_id}`}
                              title="Delete mapping"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Add account mapping */}
        <section className="bg-white border border-zinc-200 p-5">
          <div className="data-label mb-3">Add Google Ad Account Mapping</div>
          <p className="text-xs text-zinc-500 mb-4">
            Add a Google Ads customer/account ID that exists in ThoughtMetric. This lets the dashboard label & filter those campaigns correctly.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="data-label">Account ID</Label>
              <Input
                data-testid="new-account-id"
                value={newAcct.account_id}
                onChange={(e) => setNewAcct({ ...newAcct, account_id: e.target.value })}
                placeholder="e.g. 123-456-7890"
                className="rounded-none border-zinc-300 mt-1 font-mono"
              />
            </div>
            <div>
              <Label className="data-label">Name</Label>
              <Input
                data-testid="new-account-name"
                value={newAcct.name}
                onChange={(e) => setNewAcct({ ...newAcct, name: e.target.value })}
                placeholder="e.g. Edit Suits Google"
                className="rounded-none border-zinc-300 mt-1"
              />
            </div>
            <div>
              <Label className="data-label">Network</Label>
              <Select value={newAcct.network} onValueChange={(v) => setNewAcct({ ...newAcct, network: v })}>
                <SelectTrigger data-testid="new-account-network" className="rounded-none border-zinc-300 mt-1 font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="google">Google</SelectItem>
                  <SelectItem value="meta">Meta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Button onClick={addAccount} className="rounded-none w-full bg-zinc-900 hover:bg-zinc-800 font-mono uppercase tracking-wider text-xs" data-testid="add-account-btn">
                <Plus className="w-4 h-4 mr-2" /> Add
              </Button>
            </div>
          </div>
        </section>
      </div>

      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent className="rounded-none border-zinc-300" data-testid="remove-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight">Remove account mapping</AlertDialogTitle>
            <AlertDialogDescription>
              Remove the mapping for <span className="font-mono text-zinc-900">{removeTarget}</span>?
              You can re-add it anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-remove-account-btn"
              onClick={removeAccount}
              className="rounded-none bg-red-600 hover:bg-red-700"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!disconnectTarget} onOpenChange={(o) => !o && setDisconnectTarget(null)}>
        <AlertDialogContent className="rounded-none border-zinc-300" data-testid="disconnect-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight">Disconnect account</AlertDialogTitle>
            <AlertDialogDescription>
              Disconnect <span className="font-mono text-zinc-900">{disconnectTarget}</span>?
              This hides it from dashboards and stops rules from acting on it.
              You can reconnect it anytime from this page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-disconnect-btn"
              onClick={disconnectAccount}
              className="rounded-none bg-amber-600 hover:bg-amber-700"
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
